// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import api models.
import {
	Address,
} from '@app/models/api';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputAddressWithoutInputTextBoxProps,
	InputFormProps,
} from '@app/props';

interface UseInputAddressLine1Parameters {
	id: string;
	infoText?: string;
	required: boolean;
	disabled: boolean;
	value?: string;
	showValidation?: boolean;
	onSelectSuggestedAddress: (address: Address) => void;
	onGetAddressSuggestions: (query: string) => Promise<Address[]>;
}

interface UseInputAddressLine1Results<T = unknown> extends UseInputFormResult<string, InputAddressWithoutInputTextBoxProps & T> {
	setAddressLine1: (addressLine1: string) => void;
}

const useInputAddressLine1 = (params: UseInputAddressLine1Parameters): UseInputAddressLine1Results => {

	// #region exported methods.

	/**
	 * Event handler for processing single line address value changes.
	 * @param address
	 */
	const setAddressLine1 = (address: string): void => {
		other.setInput(address);
	}

	/**
	 * Returns props for form input. 
	 **/
	const applyAddressProps = (): InputAddressWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the address props.
		return {
			...formProps,
			infoText: params.infoText,
			address: other.value ?? String.empty(),
			onChangeSingleLineAddress: setAddressLine1,
			onSelectAddressSuggestion: onSelectAddressSuggestion,
			onGetAddressSuggestions: params.onGetAddressSuggestions,
		}
	};

	// #endregion exported methods.


	// #region private methods.

	const onSelectAddressSuggestion = (address: Address): void => {
		setAddressLine1(address.addressLine1 ?? String.empty());
		params.onSelectSuggestedAddress(address);
	}

	/**
	 * Validates the address.
	 * @param value
	 */
	const validateAddress = (value: string | undefined, required: boolean): ValidatorResponse => {
		if (required
			&& String.isNullOrWhiteSpace(value)) {
			return new ValidatorResponse(false, 'An answer is required.');
		}

		return new ValidatorResponse(true, String.empty());
	}

	// #endregion private methods.


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, ...other } = useInputForm<string>({
		id: params.id,
		label: 'Address Line 1',
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value ?? String.empty(),
		showValidation: params.showValidation,
		validators: [validateAddress],
	});

	// #endregion variables


	return {
		applyProps: applyAddressProps,
		setAddressLine1: setAddressLine1,
		...other,
	};
}

// Export parameters.
export {
	UseInputAddressLine1Parameters,
};

// Export results.
export {
	UseInputAddressLine1Results,
};

export default useInputAddressLine1;